import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiLock,
  FiUnlock,
  FiSearch,
  FiFileText,
  FiCalendar,
  FiShare2,
  FiChevronUp,
  FiChevronDown,
  FiClock,
  FiUser,
  FiX,
  FiMail,
  FiUpload,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import WebinarModal from '../components/WebinarModal';
import { CORRECTION_ZONE_OPTIONS, canonicalCorrectionZone } from '../constants/correctionZones';
import {
  CANONICAL_MEDICATION_NAMES,
  normalizeMedicationKey,
} from '../constants/medications';
import { peertubeStatusLabel, aiBriefStatusLabel } from '../utils/peertubeStatus';
import './Webinars.css';

const MONTH_NAMES = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

const normalizeForMatching = (value) => normalizeMedicationKey(value);

const canonicalizeNovacutanName = (rawName) => {
  const normalizedRaw = normalizeForMatching(rawName);
  if (!normalizedRaw) return '';
  const direct = CANONICAL_MEDICATION_NAMES.find(
    (canonical) => normalizeForMatching(canonical) === normalizedRaw
  );
  if (direct) return direct;
  const byContains = CANONICAL_MEDICATION_NAMES.find((canonical) => {
    const normalizedCanonical = normalizeForMatching(canonical);
    return (
      normalizedCanonical.includes(normalizedRaw) ||
      normalizedRaw.includes(normalizedCanonical)
    );
  });
  return byContains || rawName.trim();
};

/** Градиенты обложек анонсов по линейкам препаратов */
const FUTURE_COVER_PALETTE_LINE = ['#5da2c9', '#f8b4bf', '#f4f0ef'];
const FUTURE_COVER_PALETTE_FBIO = ['#243f76', '#7b4e8f', '#f4f0ef'];

const normalizeMedLabelForCover = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[-–—_]+/g, '')
    .replace(/\s+/g, '');

/** Нормализованные ключи названий FBio для обложки (NOVACUTAN … сворачивается в одну строку без пробелов) */
const FBIO_COVER_VARIANT_SUBSTRINGS = [
  'fbiolight',
  'fbiomedium',
  'fbiovolume',
  'fbiodvslight',
  'fbiodvsmedium',
  'fbiodvsvolume',
];

const medMatchesFBioAnnouncementCover = (norm) => {
  if (!norm.includes('fbio')) return false;
  return FBIO_COVER_VARIANT_SUBSTRINGS.some((sub) => norm.includes(sub));
};

/** Стабильный «случайный» градиент: палитра 243f76 / 7b4e8f / f4f0ef варьируется по id анонса + по FBio-препаратам карточки */
const hashStringForCoverSeed = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(h >>> 0);
};

const fbioAnnouncementCoverSeed = (webinarId, medicationsData) => {
  const fbKeys = (medicationsData || [])
    .map((m) => normalizeMedLabelForCover(m.name))
    .filter((norm) => medMatchesFBioAnnouncementCover(norm))
    .sort()
    .join('|');
  const part = fbKeys ? hashStringForCoverSeed(fbKeys) : 928371;
  return (Number(webinarId) || 0) * 11003 + part;
};

const LINE_PACK_COVER_KEYS = ['biopro', 'sbio', 'ybio', 'gentle', 'bright', 'prima', 'master'];

const medMatchesLinePackAnnouncementCover = (norm) => {
  if (LINE_PACK_COVER_KEYS.some((key) => norm.includes(key))) return true;
  if (norm.endsWith('eye')) return true;
  return false;
};

const getFutureAnnouncementCoverKind = (medicationsData) => {
  const list = medicationsData || [];
  if (!list.length) return null;
  for (const m of list) {
    const norm = normalizeMedLabelForCover(m.name);
    if (medMatchesFBioAnnouncementCover(norm)) return 'fbio';
  }
  for (const m of list) {
    const norm = normalizeMedLabelForCover(m.name);
    if (medMatchesLinePackAnnouncementCover(norm)) return 'line';
  }
  return null;
};

const futureAnnouncementCoverGradient = (palette, numericSeed) => {
  const perms = [
    [0, 1, 2],
    [0, 2, 1],
    [1, 0, 2],
    [1, 2, 0],
    [2, 0, 1],
    [2, 1, 0],
  ];
  const seed = numericSeed !== undefined && numericSeed !== null ? Math.abs(Number(numericSeed)) : 17;
  const [a, b, c] = perms[seed % perms.length];
  const angles = [125, 132, 142, 152, 38, 55, 72, 94, 108];
  const deg = angles[Math.floor(seed / 19) % angles.length];
  return `linear-gradient(${deg}deg, ${palette[a]} 0%, ${palette[b]} 48%, ${palette[c]} 100%)`;
};

const DEFAULT_COMPANY_COVER = {
  novacutan: { primary: '#ff6b35', secondary: '#ff8c5a' },
  cfcerussia: { primary: '#1e6bb8', secondary: '#3d8fd1' },
};

const getWebinarCompanyColors = (item) => {
  const slug = item?.company_slug || '';
  const fallback = DEFAULT_COMPANY_COVER[slug] || { primary: '#6b7280', secondary: '#9ca3af' };
  return {
    primary: item?.company_primary_color || fallback.primary,
    secondary: item?.company_secondary_color || fallback.secondary,
    name: item?.company_name || '',
    slug,
  };
};

const buildCompanyCoverGradient = (primary, secondary, seed = 0) => {
  const angles = [125, 142, 38, 72, 108];
  const deg = angles[Math.abs(Number(seed) || 0) % angles.length];
  return `linear-gradient(${deg}deg, ${primary} 0%, ${secondary} 52%, ${primary} 100%)`;
};

const formatFutureAnnouncementMSK = (iso) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const s = d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    });
    return `${s} МСК`;
  } catch {
    return '';
  }
};

const Webinars = () => {
  const { isModerator, canShare, isClient } = useAuth();
  const [searchParams] = useSearchParams();
  const [webinars, setWebinars] = useState([]);
  const [futureWebinars, setFutureWebinars] = useState([]);
  const [viewedWebinars, setViewedWebinars] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWebinar, setEditingWebinar] = useState(null);
  const initialSection = (() => {
    const sectionFromUrl = (searchParams.get('section') || '').trim();
    if (sectionFromUrl === 'future' || sectionFromUrl === 'recordings' || sectionFromUrl === 'viewed') {
      return sectionFromUrl;
    }
    return isClient ? 'recordings' : 'recordings';
  })();
  const [activeSection, setActiveSection] = useState(initialSection);
  const [showFutureForm, setShowFutureForm] = useState(false);
  const [editingFutureWebinar, setEditingFutureWebinar] = useState(null);
  const [futureFormData, setFutureFormData] = useState({
    title: '',
    description: '',
    scheduled_at: '',
    location: '',
    registration_url: '',
  });
  const [futureSpeakerIds, setFutureSpeakerIds] = useState(['']);
  const [futureMedicationIds, setFutureMedicationIds] = useState([]);
  const [futureMedicationsText, setFutureMedicationsText] = useState('');
  const [companiesBranding, setCompaniesBranding] = useState([]);
  const [companyScheduleSettings, setCompanyScheduleSettings] = useState({
    schedule_from_email: '',
    default_registration_url: '',
    name: '',
    smtp_host: '',
    smtp_configured: false,
  });
  const [scheduleEmailList, setScheduleEmailList] = useState('');
  const [scheduleEmailDays, setScheduleEmailDays] = useState(7);
  const [scheduleEmailSending, setScheduleEmailSending] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, 'saving', 'success', 'error'
  const [searchQuery, setSearchQuery] = useState('');
  const [aiSearching, setAiSearching] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showMobileFutureFilters, setShowMobileFutureFilters] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [expandedFutureIds, setExpandedFutureIds] = useState([]);
  const [clientInitialLoadDone, setClientInitialLoadDone] = useState(false);
  const [clientHubExpanded, setClientHubExpanded] = useState(false);
  const skipClientFiltersFetchOnce = useRef(false);
  const [pendingScrollSection, setPendingScrollSection] = useState(null);
  const recordingsSectionRef = useRef(null);
  const futureSectionRef = useRef(null);
  const scheduleFileInputRef = useRef(null);
  const [scheduleImportParsing, setScheduleImportParsing] = useState(false);
  const [scheduleImportSaving, setScheduleImportSaving] = useState(false);
  const [scheduleImportPreview, setScheduleImportPreview] = useState(null);
  const viewedSectionRef = useRef(null);
  const [filters, setFilters] = useState({
    year: '',
    month: '',
    speaker: '',
    medication_theory: '',
    medication_practice: '',
    correction_zone: '',
    sortBy: '-recording_date',
  });
  const [futureFilters, setFutureFilters] = useState({
    year: '',
    month: '',
    speaker: '',
    medication: '',
    sortBy: 'scheduled_at',
  });

  const splitTokens = (value) => {
    if (!value || !String(value).trim()) return [];
    return String(value)
      .split(/[,;\n]+/g)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const extractMedicationNamesFromRecordings = (items) => {
    const set = new Set();
    (items || []).forEach((webinar) => {
      splitTokens(webinar.medications_theory).forEach((token) => set.add(canonicalizeNovacutanName(token)));
      splitTokens(webinar.medications_practice).forEach((token) => set.add(canonicalizeNovacutanName(token)));
    });
    return Array.from(set).filter(Boolean);
  };

  const fetchAllRecordingMedicationNames = async () => {
    const collected = [];
    let nextUrl = '/api/webinars/?ordering=-recording_date';
    let safetyCounter = 0;

    while (nextUrl && safetyCounter < 30) {
      const response = await axios.get(nextUrl);
      const payload = response.data;
      const items = Array.isArray(payload) ? payload : (payload.results || []);
      collected.push(...extractMedicationNamesFromRecordings(items));
      nextUrl = payload && !Array.isArray(payload) ? payload.next : null;
      safetyCounter += 1;
    }

    return Array.from(new Set(collected));
  };

  const correctionZonesOptions = useMemo(() => {
    const set = new Set(CORRECTION_ZONE_OPTIONS);
    webinars.forEach((w) => {
      splitTokens(w.correction_zones).forEach((t) => {
        const canon = canonicalCorrectionZone(t);
        if (canon) set.add(canon);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [webinars]);

  const medicationsTheoryOptions = useMemo(() => {
    const set = new Set();
    webinars.forEach((w) => splitTokens(w.medications_theory).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [webinars]);

  const medicationsPracticeOptions = useMemo(() => {
    const set = new Set();
    webinars.forEach((w) => splitTokens(w.medications_practice).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [webinars]);

  const getRecordingPreview = (webinar) => {
    const sourceText = (webinar.summary || webinar.ai_topic || webinar.transcription || '').trim();
    if (!sourceText) return '';
    const normalized = sourceText.replace(/\s+/g, ' ').trim();
    return normalized.length > 160 ? `${normalized.slice(0, 160)}...` : normalized;
  };

  const fetchWebinars = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      // Добавляем параметры только если они не пустые
      if (filters.year && filters.year !== '') {
        params.append('year', filters.year);
      }
      if (filters.month && filters.month !== '') {
        params.append('month', filters.month);
      }
      if (filters.speaker && filters.speaker !== '') {
        params.append('speaker', filters.speaker);
      }
      if (filters.medication_theory && filters.medication_theory.trim() !== '') {
        params.append('medication_theory', filters.medication_theory.trim());
      }
      if (filters.medication_practice && filters.medication_practice.trim() !== '') {
        params.append('medication_practice', filters.medication_practice.trim());
      }
      if (filters.correction_zone && filters.correction_zone.trim() !== '') {
        params.append('correction_zone', filters.correction_zone.trim());
      }
      if (filters.sortBy && filters.sortBy !== '') {
        params.append('ordering', filters.sortBy);
      }

      const queryString = params.toString();
      const url = queryString ? `/api/webinars/?${queryString}` : '/api/webinars/';
      const response = await axios.get(url);
      setWebinars(response.data.results || response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Требуется авторизация. Пожалуйста, войдите в систему.');
      } else {
        toast.error('Ошибка загрузки записей вебинаров');
      }
      console.error('Error fetching webinars:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchFutureWebinars = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await axios.get('/api/future-webinars/?ordering=scheduled_at');
      setFutureWebinars(response.data.results || response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Требуется авторизация. Пожалуйста, войдите в систему.');
      } else {
        toast.error('Ошибка загрузки будущих вебинаров');
      }
      console.error('Error fetching future webinars:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchViewedWebinars = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await axios.get('/api/webinars/viewed/');
      setViewedWebinars(response.data || []);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Требуется авторизация. Пожалуйста, войдите в систему.');
      } else {
        toast.error('Ошибка загрузки просмотренных вебинаров');
      }
      console.error('Error fetching viewed webinars:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isClient) return;
    if (activeSection === 'recordings') {
      fetchWebinars();
    }
  }, [filters, activeSection, isClient]);

  useEffect(() => {
    if (isClient || !isModerator) return;
    const onScopeChange = () => {
      if (activeSection === 'recordings') fetchWebinars();
      if (activeSection === 'future') fetchFutureWebinars();
    };
    window.addEventListener('moderator-company-scope-changed', onScopeChange);
    return () => window.removeEventListener('moderator-company-scope-changed', onScopeChange);
  }, [isClient, isModerator, activeSection]);

  useEffect(() => {
    if (isClient) return;
    if (activeSection === 'future') {
      fetchFutureWebinars();
    }
  }, [activeSection, isClient]);

  useEffect(() => {
    if (isClient) return;
    if (activeSection === 'viewed') {
      fetchViewedWebinars();
    }
  }, [activeSection, isClient]);

  useEffect(() => {
    if (!isClient) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchWebinars(false),
          fetchFutureWebinars(false),
          fetchViewedWebinars(false),
        ]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setClientInitialLoadDone(true);
          skipClientFiltersFetchOnce.current = true;
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isClient]);

  useEffect(() => {
    if (!isClient || !clientInitialLoadDone) return;
    if (skipClientFiltersFetchOnce.current) {
      skipClientFiltersFetchOnce.current = false;
      return;
    }
    fetchWebinars(false);
  }, [filters, isClient, clientInitialLoadDone]);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (isModerator) {
      axios.get('/api/companies/').then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        setCompaniesBranding(list);
      }).catch(() => setCompaniesBranding([]));
      axios.get('/api/companies/current/').then((r) => {
        setCompanyScheduleSettings({
          schedule_from_email: r.data.schedule_from_email || '',
          default_registration_url: r.data.default_registration_url || '',
          name: r.data.name || '',
          smtp_host: r.data.smtp_host || '',
          smtp_configured: Boolean(r.data.smtp_configured),
        });
      }).catch(() => {});
    }
  }, [isModerator]);

  const companyLegendItems = useMemo(() => {
    if (!isModerator) return [];
    if (companiesBranding.length) {
      return companiesBranding.map((c) => ({
        id: c.id,
        name: c.name,
        slug: c.slug,
        primary: c.primary_color || DEFAULT_COMPANY_COVER[c.slug]?.primary || '#6b7280',
        secondary: c.secondary_color || DEFAULT_COMPANY_COVER[c.slug]?.secondary || '#9ca3af',
      }));
    }
    const seen = new Map();
    webinars.forEach((w) => {
      const c = getWebinarCompanyColors(w);
      if (c.name && !seen.has(w.company)) {
        seen.set(w.company, { id: w.company, name: c.name, slug: c.slug, primary: c.primary, secondary: c.secondary });
      }
    });
    return [...seen.values()];
  }, [isModerator, companiesBranding, webinars]);

  useEffect(() => {
    axios.get('/api/speakers/').then((r) => {
      const list = r.data.results || r.data;
      setSpeakers(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/api/medications/').then((r) => {
      const list = r.data.results || r.data;
      setMedications(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  const groupByMonthYear = (list) => {
    const groups = {};
    list.forEach((w) => {
      const d = new Date(w.recording_date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!groups[key]) groups[key] = { year: d.getFullYear(), month: d.getMonth(), items: [] };
      groups[key].items.push(w);
    });
    return Object.keys(groups)
      .sort((a, b) => {
        const [yearA, monthA] = a.split('-').map(Number);
        const [yearB, monthB] = b.split('-').map(Number);
        if (yearB !== yearA) return yearB - yearA;
        return monthB - monthA;
      })
      .map((key) => groups[key]);
  };

  const uniqueYears = [...new Set(webinars.map((w) => new Date(w.recording_date).getFullYear()))].sort((a, b) => b - a);
  const futureUniqueYears = [...new Set(futureWebinars.map((w) => new Date(w.scheduled_at).getFullYear()))].sort((a, b) => b - a);

  const filteredFutureWebinars = useMemo(() => {
    let items = [...futureWebinars];
    if (futureFilters.year) {
      items = items.filter((item) => new Date(item.scheduled_at).getFullYear() === parseInt(futureFilters.year, 10));
    }
    if (futureFilters.month) {
      items = items.filter((item) => new Date(item.scheduled_at).getMonth() + 1 === parseInt(futureFilters.month, 10));
    }
    if (futureFilters.speaker) {
      items = items.filter((item) =>
        (item.speakers_data || []).some((speaker) => String(speaker.id) === String(futureFilters.speaker))
      );
    }
    if (futureFilters.medication) {
      items = items.filter((item) =>
        (item.medications_data || []).some((medication) => String(medication.id) === String(futureFilters.medication))
      );
    }

    const sortKey = futureFilters.sortBy || 'scheduled_at';
    items.sort((a, b) => {
      if (sortKey === 'scheduled_at') return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      if (sortKey === '-scheduled_at') return new Date(b.scheduled_at) - new Date(a.scheduled_at);
      if (sortKey === 'title') return (a.title || '').localeCompare(b.title || '', 'ru');
      if (sortKey === '-title') return (b.title || '').localeCompare(a.title || '', 'ru');
      return 0;
    });
    return items;
  }, [futureWebinars, futureFilters]);

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот вебинар?')) {
      return;
    }

    try {
      await axios.delete(`/api/webinars/${id}/`);
      toast.success('Вебинар удален');
      fetchWebinars();
    } catch (error) {
      toast.error('Ошибка удаления вебинара');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await axios.post(`/api/webinars/${id}/toggle_status/`);
      toast.success('Статус изменен');
      fetchWebinars();
    } catch (error) {
      toast.error('Ошибка изменения статуса');
    }
  };

  const handleGenerateTranscription = async (id) => {
    try {
      const response = await axios.post(`/api/webinars/${id}/generate-transcription/`);
      
      // Если есть предупреждение о существующей транскрипции
      if (response.data.warning && response.data.has_existing_transcription) {
        const confirmMessage = `${response.data.message}\n\nДлина существующей транскрипции: ${response.data.existing_length} символов`;
        const shouldRegenerate = window.confirm(confirmMessage);
        
        if (shouldRegenerate) {
          // Отправляем запрос с подтверждением перезаписи
          const regenerateResponse = await axios.post(`/api/webinars/${id}/generate-transcription/`, {
            force_regenerate: true
          });
          toast.success(regenerateResponse.data.message || 'Генерация новой транскрипции запущена. После готовности она будет отправлена на email администратора в формате TXT файла.');
        } else {
          toast.info('Генерация транскрипции отменена');
        }
      } else {
        // Обычный случай - транскрипции нет
        toast.success(response.data.message || 'Генерация транскрипции запущена. После готовности она будет отправлена на email администратора в формате TXT файла.');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка запуска генерации транскрипции');
    }
  };

  const handleEdit = (webinar) => {
    setEditingWebinar(webinar);
    setModalOpen(true);
    setShowFutureForm(false);
    setEditingFutureWebinar(null);
  };

  const handleCloseModal = (success = false) => {
    setModalOpen(false);
    setEditingWebinar(null);
    if (success) {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    }
    fetchWebinars();
  };

  const parseScheduleEmails = (input) => {
    const raw = String(input ?? '').trim();
    if (!raw) return [];
    return raw
      .replace(/[,;\n\r]+/g, ' ')
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
  };

  const handleSendScheduleEmail = async () => {
    const emails = parseScheduleEmails(scheduleEmailList);
    if (!emails.length) {
      toast.error('Введите хотя бы один email');
      return;
    }
    setScheduleEmailSending(true);
    try {
      const response = await axios.post('/api/future-webinars/send-schedule-email/', {
        emails,
        days_ahead: scheduleEmailDays,
      });
      toast.success(response.data?.message || 'Расписание отправлено');
      setScheduleEmailList('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка отправки расписания');
    } finally {
      setScheduleEmailSending(false);
    }
  };

  const resetFutureForm = () => {
    setFutureFormData({
      title: '',
      description: '',
      scheduled_at: '',
      location: '',
      registration_url: companyScheduleSettings.default_registration_url || '',
    });
    setFutureSpeakerIds(['']);
    setFutureMedicationIds([]);
    setFutureMedicationsText('');
    setEditingFutureWebinar(null);
    setShowFutureForm(false);
  };

  const buildFuturePayload = () => {
    const uniqueSpeakerIds = Array.from(
      new Set(futureSpeakerIds.filter(Boolean).map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id)))
    );
    const uniqueMedicationIds = Array.from(
      new Set(futureMedicationIds.filter(Boolean).map((id) => parseInt(id, 10)).filter((id) => !Number.isNaN(id)))
    );
    return {
      ...futureFormData,
      speakers: uniqueSpeakerIds,
      medications: uniqueMedicationIds,
    };
  };

  const handleCreateOrUpdateFutureWebinar = async (e) => {
    e.preventDefault();
    if (!futureFormData.title.trim()) {
      toast.error('Введите тему будущего вебинара');
      return;
    }
    if (!futureFormData.scheduled_at) {
      toast.error('Выберите дату и время');
      return;
    }
    try {
      const payload = buildFuturePayload();
      if (editingFutureWebinar) {
        await axios.patch(`/api/future-webinars/${editingFutureWebinar.id}/`, payload);
        toast.success('Будущий вебинар обновлен');
      } else {
        await axios.post('/api/future-webinars/', payload);
        toast.success('Будущий вебинар добавлен');
      }
      resetFutureForm();
      fetchFutureWebinars(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения будущего вебинара');
    }
  };

  const handleScheduleFileSelected = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setScheduleImportParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/future-webinars/parse-schedule/', formData);
      setScheduleImportPreview(response.data);
      toast.success(`Найдено мероприятий: ${response.data.count}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось разобрать файл расписания');
    } finally {
      setScheduleImportParsing(false);
    }
  };

  const toggleScheduleImportItem = (index) => {
    setScheduleImportPreview((prev) => {
      if (!prev?.items) return prev;
      const items = prev.items.map((item, i) => (
        i === index ? { ...item, selected: !item.selected } : item
      ));
      return { ...prev, items };
    });
  };

  const handleConfirmScheduleImport = async () => {
    const selectedItems = (scheduleImportPreview?.items || []).filter((item) => item.selected && !item.is_duplicate);
    if (!selectedItems.length) {
      toast.error('Выберите хотя бы одно мероприятие');
      return;
    }
    setScheduleImportSaving(true);
    try {
      const response = await axios.post('/api/future-webinars/import-schedule/', { items: selectedItems });
      const created = response.data.created_count || 0;
      const skipped = response.data.skipped_count || 0;
      toast.success(`Добавлено в расписание: ${created}${skipped ? `, пропущено: ${skipped}` : ''}`);
      setScheduleImportPreview(null);
      fetchFutureWebinars(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Не удалось добавить расписание');
    } finally {
      setScheduleImportSaving(false);
    }
  };

  const handleEditFutureWebinar = (item) => {
    setEditingFutureWebinar(item);
    setFutureFormData({
      title: item.title || '',
      description: item.description || '',
      scheduled_at: item.scheduled_at ? item.scheduled_at.slice(0, 16) : '',
      location: item.location || '',
      registration_url: item.registration_url || '',
    });
    setFutureSpeakerIds(
      item.speakers_data?.length ? item.speakers_data.map((speaker) => String(speaker.id)) : ['']
    );
    setFutureMedicationIds(
      item.medications_data?.length ? item.medications_data.map((medication) => String(medication.id)) : []
    );
    setFutureMedicationsText(
      item.medications_data?.length ? item.medications_data.map((medication) => medication.name).join(', ') : ''
    );
    setShowFutureForm(true);
    setModalOpen(false);
    setEditingWebinar(null);
  };

  const handleDeleteFutureWebinar = async (id) => {
    if (!window.confirm('Удалить будущий вебинар из расписания?')) {
      return;
    }
    try {
      await axios.delete(`/api/future-webinars/${id}/`);
      toast.success('Будущий вебинар удален');
      fetchFutureWebinars(false);
    } catch (error) {
      toast.error('Ошибка удаления будущего вебинара');
    }
  };

  const addFutureSpeakerField = () => {
    setFutureSpeakerIds([...futureSpeakerIds, '']);
  };

  const updateFutureSpeakerField = (index, value) => {
    const next = [...futureSpeakerIds];
    next[index] = value;
    setFutureSpeakerIds(next);
  };

  const removeFutureSpeakerField = (index) => {
    const next = futureSpeakerIds.filter((_, i) => i !== index);
    setFutureSpeakerIds(next.length ? next : ['']);
  };

  const getGoogleCalendarUrl = (item) => {
    const start = new Date(item.scheduled_at);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const toGCalDate = (date) =>
      date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const details = [
      item.description || '',
      item.registration_url ? `Регистрация: ${item.registration_url}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: item.title,
      dates: `${toGCalDate(start)}/${toGCalDate(end)}`,
      details,
      location: item.location || '',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDuration = (seconds) => {
    const total = Math.max(0, parseInt(seconds || 0, 10));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const viewedByWebinarId = useMemo(() => {
    const map = new Map();
    (viewedWebinars || []).forEach((entry) => {
      const webinarId = entry?.webinar?.id;
      if (!webinarId) return;
      const existing = map.get(webinarId);
      if (!existing) {
        map.set(webinarId, entry);
        return;
      }
      const existingDate = existing?.last_viewed_at ? new Date(existing.last_viewed_at).getTime() : 0;
      const currentDate = entry?.last_viewed_at ? new Date(entry.last_viewed_at).getTime() : 0;
      if (currentDate > existingDate) {
        map.set(webinarId, entry);
      }
    });
    return map;
  }, [viewedWebinars]);

  const inProgressCount = useMemo(
    () => (viewedWebinars || []).filter((entry) => !entry?.is_completed && (entry?.last_position_seconds || 0) > 0).length,
    [viewedWebinars]
  );

  const nearestFutureLabel = useMemo(() => {
    const now = new Date();
    const nearest = (futureWebinars || [])
      .filter((item) => {
        const dt = new Date(item.scheduled_at);
        return !Number.isNaN(dt.getTime()) && dt >= now;
      })
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
    if (!nearest) return null;
    return new Date(nearest.scheduled_at).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, [futureWebinars]);

  const newRecordingsCount = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return (webinars || []).filter((item) => {
      const dt = new Date(item.recording_date);
      return !Number.isNaN(dt.getTime()) && dt >= weekAgo;
    }).length;
  }, [webinars]);

  const hubNearestFuturePreview = useMemo(() => {
    const now = new Date();
    return filteredFutureWebinars
      .filter((item) => {
        const dt = new Date(item.scheduled_at);
        return !Number.isNaN(dt.getTime()) && dt >= now;
      })
      .slice(0, 3);
  }, [filteredFutureWebinars]);

  const hubInProgressPreview = useMemo(
    () =>
      (viewedWebinars || [])
        .filter(
          (entry) =>
            entry?.webinar?.id &&
            !entry?.is_completed &&
            (entry?.last_position_seconds || 0) > 0
        )
        .slice(0, 10),
    [viewedWebinars]
  );

  const hubNewRecordingsPreview = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return [...(webinars || [])]
      .filter((item) => {
        const dt = new Date(item.recording_date);
        return !Number.isNaN(dt.getTime()) && dt >= weekAgo;
      })
      .sort((a, b) => new Date(b.recording_date) - new Date(a.recording_date))
      .slice(0, 10);
  }, [webinars]);

  const handleHeroChipClick = (section) => {
    setActiveSection(section);
    setPendingScrollSection(section);
  };

  useEffect(() => {
    if (!pendingScrollSection || activeSection !== pendingScrollSection || loading) return;
    const sectionRefMap = {
      recordings: recordingsSectionRef,
      future: futureSectionRef,
      viewed: viewedSectionRef,
    };
    const targetRef = sectionRefMap[pendingScrollSection];
    if (targetRef?.current) {
      window.setTimeout(() => {
        targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
      setPendingScrollSection(null);
    }
  }, [activeSection, pendingScrollSection, loading]);

  const heroSubtitle = useMemo(() => {
    const parts = [];
    parts.push(`${inProgressCount} в процессе`);
    if (nearestFutureLabel) parts.push(`Ближайший: ${nearestFutureLabel}`);
    parts.push(`${newRecordingsCount} новых записей за 7 дней`);
    return parts.join(' · ');
  }, [inProgressCount, nearestFutureLabel, newRecordingsCount]);

  const handleShareFutureWebinar = async (item) => {
    if (!item?.registration_url) {
      toast.error('Для этого вебинара не указана ссылка на регистрацию');
      return;
    }
    const dateText = new Date(item.scheduled_at).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const speakersText = item.speakers_data?.length
      ? item.speakers_data.map((speaker) => speaker.full_name).join(', ')
      : '';
    let text = `Приглашаем на вебинар\n`;
    text += `Тема: ${item.title}\n`;
    text += `Дата: ${dateText}\n`;
    if (speakersText) {
      text += `Спикеры: ${speakersText}\n`;
    }
    if (item.location) {
      text += `Адрес: ${item.location}\n`;
    }
    if (item.description) {
      text += `Описание: ${item.description}\n`;
    }
    text += `Регистрация: ${item.registration_url}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      toast.success('Текст приглашения скопирован');
    } catch (error) {
      toast.error('Не удалось скопировать текст');
    }
  };

  const handleAiSearchSubmit = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setAiSearching(true);
    try {
      const response = await axios.post('/api/search/ai-dialog/', {
        query: searchQuery,
      });
      setWebinars(response.data.results || []);
      toast.success(`Найдено вебинаров: ${response.data.results?.length || 0}`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Ошибка поиска');
    } finally {
      setAiSearching(false);
    }
  };

  const closeAiSearch = () => {
    setSearchQuery('');
    fetchWebinars(false);
  };

  const toggleFutureDetails = (id) => {
    setExpandedFutureIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const applyFutureMedicationsFromText = async () => {
    const parts = futureMedicationsText
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    if (!parts.length) {
      setFutureMedicationIds([]);
      return;
    }
    const normalizeNovacutanTail = (value) =>
      normalizeForMatching(value).replace(/^novacutan|^новакутан/, '');
    let dictionaryFromRecordings = extractMedicationNamesFromRecordings(webinars);
    if (!dictionaryFromRecordings.length) {
      try {
        dictionaryFromRecordings = await fetchAllRecordingMedicationNames();
      } catch (error) {
        dictionaryFromRecordings = [];
      }
    }
    if (!medications.length) {
      const normalizedRecordingMap = new Map(
        dictionaryFromRecordings.map((name) => [normalizeForMatching(name), name])
      );
      const normalizedTextNames = parts.map((name) => canonicalizeNovacutanName(name));
      const namesToCreate = Array.from(
        new Set(
          normalizedTextNames
            .map((name) => {
              const normalizedName = normalizeForMatching(name);
              if (normalizedRecordingMap.has(normalizedName)) {
                return normalizedRecordingMap.get(normalizedName);
              }
              const nameTail = normalizeNovacutanTail(name);
              const fuzzyRecording = dictionaryFromRecordings.find((recordingName) => {
                const normalizedRecording = normalizeForMatching(recordingName);
                const recordingTail = normalizeNovacutanTail(recordingName);
                return (
                  normalizedRecording.includes(normalizedName) ||
                  normalizedName.includes(normalizedRecording) ||
                  (nameTail && recordingTail.includes(nameTail)) ||
                  (nameTail && nameTail.includes(recordingTail))
                );
              });
              return fuzzyRecording || null;
            })
            .filter(Boolean)
        )
      );

      if (!namesToCreate.length) {
        setFutureMedicationIds([]);
        setFutureMedicationsText(Array.from(new Set(normalizedTextNames)).join(', '));
        toast.info('Препараты не найдены ни в справочнике, ни в записях вебинаров.');
        return;
      }

      try {
        await Promise.all(
          namesToCreate.map((name) =>
            axios.post('/api/medications/', { name }).catch(() => null)
          )
        );
        const medsResponse = await axios.get('/api/medications/');
        const medsList = medsResponse.data.results || medsResponse.data || [];
        setMedications(Array.isArray(medsList) ? medsList : []);
        toast.success('Справочник препаратов обновлен из записей вебинаров. Повторите выбор.');
      } catch (error) {
        setFutureMedicationIds([]);
        setFutureMedicationsText(Array.from(new Set(normalizedTextNames)).join(', '));
        toast.info('Не удалось автоматически заполнить справочник. Добавьте препараты в админке.');
      }
      return;
    }
    const byName = new Map(
      medications.map((medication) => [
        normalizeForMatching(medication.name),
        String(medication.id),
      ])
    );
    const foundIds = [];
    const notFound = [];
    const normalizedTextNames = [];
    parts.forEach((name) => {
      const canonicalName = canonicalizeNovacutanName(name);
      normalizedTextNames.push(canonicalName);
      const normalizedCanonical = normalizeForMatching(canonicalName);
      const normalizedOriginal = normalizeForMatching(name);
      const canonicalTail = normalizeNovacutanTail(canonicalName);
      const originalTail = normalizeNovacutanTail(name);
      let id = byName.get(normalizedCanonical) || byName.get(normalizedOriginal);
      if (!id) {
        const fuzzyMatch = medications.find((medication) => {
          const normalizedMedication = normalizeForMatching(medication.name);
          const medicationTail = normalizeNovacutanTail(medication.name);
          return (
            normalizedMedication.includes(normalizedCanonical) ||
            normalizedCanonical.includes(normalizedMedication) ||
            normalizedMedication.includes(normalizedOriginal) ||
            normalizedOriginal.includes(normalizedMedication) ||
            (canonicalTail && medicationTail.includes(canonicalTail)) ||
            (canonicalTail && canonicalTail.includes(medicationTail)) ||
            (originalTail && medicationTail.includes(originalTail)) ||
            (originalTail && originalTail.includes(medicationTail))
          );
        });
        if (fuzzyMatch) {
          id = String(fuzzyMatch.id);
        } else {
          const fromRecordings = dictionaryFromRecordings.find((recordingName) => {
            const normalizedRecording = normalizeForMatching(recordingName);
            const recordingTail = normalizeNovacutanTail(recordingName);
            return (
              normalizedRecording.includes(normalizedCanonical) ||
              normalizedCanonical.includes(normalizedRecording) ||
              normalizedRecording.includes(normalizedOriginal) ||
              normalizedOriginal.includes(normalizedRecording) ||
              (canonicalTail && recordingTail.includes(canonicalTail)) ||
              (canonicalTail && canonicalTail.includes(recordingTail)) ||
              (originalTail && recordingTail.includes(originalTail)) ||
              (originalTail && originalTail.includes(recordingTail))
            );
          });
          if (fromRecordings) {
            const createdMedication = medications.find(
              (medication) => normalizeForMatching(medication.name) === normalizeForMatching(fromRecordings)
            );
            if (createdMedication) {
              id = String(createdMedication.id);
            } else {
              // Keep as not found; user can normalize via dictionary.
            }
          }
        }
      }
      if (id) {
        foundIds.push(id);
      } else {
        notFound.push(canonicalName);
      }
    });
    setFutureMedicationIds(Array.from(new Set(foundIds)));
    setFutureMedicationsText(Array.from(new Set(normalizedTextNames)).join(', '));
    if (notFound.length) {
      toast.info(`Не найдены в справочнике: ${notFound.join(', ')}`);
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className={`webinars-page ${isClient ? 'client-webinars-page' : ''}`}>
      <div className="webinars-header-sticky">
        <div className={`webinars-section-tabs ${isClient ? 'client-tabs-row' : ''}`}>
          <button
            type="button"
            className={`section-tab ${activeSection === 'future' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('future');
              setModalOpen(false);
              setEditingWebinar(null);
            }}
          >
            {isClient ? 'Анонсы' : 'Будущие вебинары'}
          </button>
          <button
            type="button"
            className={`section-tab ${activeSection === 'recordings' ? 'active' : ''}`}
            onClick={() => {
              setActiveSection('recordings');
              setShowFutureForm(false);
              setEditingFutureWebinar(null);
            }}
          >
            {isClient ? 'Архив' : 'Записи вебинаров'}
          </button>
          {isClient && (
            <button
              type="button"
              className={`section-tab ${activeSection === 'viewed' ? 'active' : ''}`}
              onClick={() => {
                setActiveSection('viewed');
                setModalOpen(false);
                setShowFutureForm(false);
              }}
            >
              История
            </button>
          )}
        </div>
        <div className="page-header">
          {!(isClient && (activeSection === 'future' || activeSection === 'recordings')) && (
            <h1>
              {activeSection === 'future'
                ? 'Будущие вебинары'
                : activeSection === 'viewed'
                  ? 'Просмотренные вебинары'
                  : 'Записи вебинаров'}
            </h1>
          )}
          {activeSection === 'recordings' && !isClient && (
            <form className="header-ai-search" onSubmit={handleAiSearchSubmit}>
              <input
                type="text"
                className="header-ai-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='ИИ-поиск по записям...'
              />
              <button type="submit" className="header-ai-search-btn" disabled={aiSearching} title="Найти">
                <FiSearch />
              </button>
              <button type="button" className="header-ai-search-btn close" onClick={closeAiSearch} title="Закрыть">
                <FiX />
              </button>
            </form>
          )}
          {isModerator && activeSection === 'recordings' && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditingWebinar(null);
                setModalOpen(true);
                setShowFutureForm(false);
                setEditingFutureWebinar(null);
              }}
            >
              <FiPlus /> Загрузить запись
            </button>
          )}
          {isModerator && activeSection === 'future' && (
            <div className="page-header-actions">
              <input
                ref={scheduleFileInputRef}
                type="file"
                accept=".doc,.docx,.pdf,.txt,.rtf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                hidden
                onChange={handleScheduleFileSelected}
              />
              <button
                type="button"
                className="btn btn-outline"
                disabled={scheduleImportParsing}
                onClick={() => scheduleFileInputRef.current && scheduleFileInputRef.current.click()}
              >
                <FiUpload /> {scheduleImportParsing ? 'Разбор файла...' : 'Загрузить файл'}
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setModalOpen(false);
                  setEditingWebinar(null);
                  setShowFutureForm(true);
                }}
              >
                <FiPlus /> Добавить в расписание
              </button>
            </div>
          )}
        </div>

        {activeSection === 'recordings' && (
          <>
      <>
      <div className="mobile-filters-toggle-wrap">
        <button
          type="button"
          className="btn btn-outline mobile-filters-toggle-btn"
          onClick={() => setShowMobileFilters((prev) => !prev)}
        >
          Показать фильтры
        </button>
      </div>
      <div className={`webinars-filters ${showMobileFilters ? 'mobile-open' : ''}`}>
        <select
          value={filters.year}
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
          className="filter-select"
        >
          <option value="">Все годы</option>
          {uniqueYears.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={filters.month}
          onChange={(e) => setFilters({ ...filters, month: e.target.value })}
          className="filter-select"
        >
          <option value="">Все месяцы</option>
          {MONTH_NAMES.map((name, i) => (
            <option key={i} value={i + 1}>{name}</option>
          ))}
        </select>
        <select
          value={filters.speaker}
          onChange={(e) => setFilters({ ...filters, speaker: e.target.value })}
          className="filter-select"
        >
          <option value="">Все спикеры</option>
          {speakers.map((s) => (
            <option key={s.id} value={s.id}>{s.full_name}</option>
          ))}
        </select>
        <select
          value={filters.medication_theory}
          onChange={(e) => setFilters({ ...filters, medication_theory: e.target.value })}
          className="filter-select"
        >
          <option value="">Препараты теория (все)</option>
          {medicationsTheoryOptions.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
        <select
          value={filters.medication_practice}
          onChange={(e) => setFilters({ ...filters, medication_practice: e.target.value })}
          className="filter-select"
        >
          <option value="">Препараты практика (все)</option>
          {medicationsPracticeOptions.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
        <select
          value={filters.correction_zone}
          onChange={(e) => setFilters({ ...filters, correction_zone: e.target.value })}
          className="filter-select"
        >
          <option value="">Зоны коррекции (все)</option>
          {filters.correction_zone && !correctionZonesOptions.includes(filters.correction_zone) && (
            <option value={filters.correction_zone}>{filters.correction_zone}</option>
          )}
          {correctionZonesOptions.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
        <select
          value={filters.sortBy}
          onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
          className="filter-select"
        >
          <option value="-recording_date">Сначала новые</option>
          <option value="recording_date">Сначала старые</option>
          <option value="-views_count">По просмотрам</option>
          <option value="title">По теме (А-Я)</option>
          <option value="-title">По теме (Я-А)</option>
          <option value="speaker__last_name">По спикеру (А-Я)</option>
          <option value="-speaker__last_name">По спикеру (Я-А)</option>
        </select>
      </div>
      </>

      {(
        filters.year ||
        filters.month ||
        filters.speaker ||
        filters.medication_theory ||
        filters.medication_practice ||
        filters.correction_zone
      ) && (
        <div className="filters-reset">
          <button
            className="btn btn-outline"
            onClick={() => {
              setFilters({
                year: '',
                month: '',
                speaker: '',
                medication_theory: '',
                medication_practice: '',
                correction_zone: '',
                sortBy: '-recording_date',
              });
            }}
          >
            Сбросить фильтры
          </button>
        </div>
      )}
          </>
        )}
      </div>

      {isClient && (
        <div className="client-hub-hero">
          <button
            type="button"
            className="client-hub-hero-head"
            onClick={() => setClientHubExpanded((v) => !v)}
            aria-expanded={clientHubExpanded}
          >
            <span className="client-hub-hero-title">Что посмотреть сегодня</span>
            <span className="client-hub-hero-chevron" aria-hidden>
              {clientHubExpanded ? <FiChevronUp /> : <FiChevronDown />}
            </span>
          </button>
          <div className="client-hub-hero-subtitle">{heroSubtitle}</div>
          {clientHubExpanded && (
            <div className="client-hub-expanded">
              <div className="client-hub-expand-section">
                <div className="client-hub-expand-title">Ближайшие вебинары</div>
                {hubNearestFuturePreview.length > 0 ? (
                  <ul className="client-hub-expand-list">
                    {hubNearestFuturePreview.map((item) => (
                      <li key={item.id} className="client-hub-expand-item">
                        <span className="client-hub-expand-item-title">{item.title}</span>
                        <span className="client-hub-expand-item-meta">
                          {new Date(item.scheduled_at).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="client-hub-expand-empty">Пока нет запланированных вебинаров в ленте</p>
                )}
                <button
                  type="button"
                  className="client-hub-expand-link btn btn-outline btn-sm"
                  onClick={() => handleHeroChipClick('future')}
                >
                  Открыть анонсы
                </button>
              </div>
              <div className="client-hub-expand-section">
                <div className="client-hub-expand-title">В процессе</div>
                {hubInProgressPreview.length > 0 ? (
                  <ul className="client-hub-expand-list">
                    {hubInProgressPreview.map((entry) => (
                      <li key={entry.id} className="client-hub-expand-item">
                        <Link
                          className="client-hub-expand-item-link"
                          to={`/webinars/${entry.webinar.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {entry.webinar?.title || 'Вебинар'}
                        </Link>
                        <span className="client-hub-expand-item-meta">
                          с {formatDuration(entry.last_position_seconds)}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="client-hub-expand-empty">Нет записей «в процессе»</p>
                )}
                <button
                  type="button"
                  className="client-hub-expand-link btn btn-outline btn-sm"
                  onClick={() => handleHeroChipClick('viewed')}
                >
                  Открыть историю
                </button>
              </div>
              <div className="client-hub-expand-section">
                <div className="client-hub-expand-title">Новые записи (7 дней)</div>
                {hubNewRecordingsPreview.length > 0 ? (
                  <ul className="client-hub-expand-list">
                    {hubNewRecordingsPreview.map((w) => (
                      <li key={w.id} className="client-hub-expand-item">
                        <Link className="client-hub-expand-item-link" to={`/webinars/${w.id}`}>
                          {w.title}
                        </Link>
                        <span className="client-hub-expand-item-meta">
                          {new Date(w.recording_date).toLocaleDateString('ru-RU')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="client-hub-expand-empty">За неделю новых записей нет</p>
                )}
                <button
                  type="button"
                  className="client-hub-expand-link btn btn-outline btn-sm"
                  onClick={() => handleHeroChipClick('recordings')}
                >
                  Открыть архив
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {(isClient || activeSection === 'recordings') && (
        <div className={isClient && activeSection !== 'recordings' ? 'client-tab-hidden' : ''}>
        <>
        <div ref={recordingsSectionRef} />
        {isModerator && companyLegendItems.length > 0 && (
          <div className="company-cover-legend" aria-label="Цвета фирм">
            {companyLegendItems.map((c) => (
              <span key={c.id || c.slug} className="company-cover-legend-item">
                <span
                  className="company-cover-legend-swatch"
                  style={{ background: buildCompanyCoverGradient(c.primary, c.secondary, c.id) }}
                />
                {c.name}
              </span>
            ))}
          </div>
        )}
        {webinars.length > 0 ? (
        groupByMonthYear(webinars).map((group) => (
          <div key={`${group.year}-${group.month}`} className="webinars-group">
            <h2 className="webinars-group-title">
              {MONTH_NAMES[group.month]} {group.year}
            </h2>
            <div className={`webinars-grid ${isClient ? 'client-recordings-grid' : ''}`}>
              {group.items.map((webinar) => {
            const companyCover = getWebinarCompanyColors(webinar);
            const showCompanyCover = isModerator && companyCover.name;
            return (
            <div
              key={webinar.id}
              className={`webinar-card ${isClient ? 'client-recording-card' : ''} ${showCompanyCover ? 'webinar-card--company-cover' : ''}`}
            >
              {showCompanyCover && (
                <Link
                  to={`/webinars/${webinar.id}`}
                  className="webinar-company-cover"
                  style={{ background: buildCompanyCoverGradient(companyCover.primary, companyCover.secondary, webinar.id) }}
                >
                  <span className="webinar-company-cover-badge">{companyCover.name}</span>
                  <h3 className="webinar-company-cover-title">{webinar.title}</h3>
                </Link>
              )}
              <div className="webinar-card-header">
                <div className="webinar-status">
                  {!isClient && (
                    <span className={`status-badge ${webinar.status}`}>
                      {webinar.status === 'open' ? 'Открыт' : 'Закрыт'}
                    </span>
                  )}
                  {!isClient && webinar.peertube_status && webinar.peertube_status !== 'none' && (
                    <span className={`status-badge peertube-${webinar.peertube_status}`}>
                      {peertubeStatusLabel(webinar.peertube_status)}
                    </span>
                  )}
                  {!isClient && webinar.ai_brief_status && webinar.ai_brief_status !== 'none' && webinar.ai_brief_status !== 'ready' && (
                    <span className={`status-badge peertube-${webinar.ai_brief_status === 'error' ? 'error' : 'processing'}`}>
                      {aiBriefStatusLabel(webinar.ai_brief_status)}
                    </span>
                  )}
                  {webinar.is_private && <FiLock className="lock-icon" />}
                </div>
                {isModerator && (
                  <div className="webinar-actions">
                    <button
                      className="icon-btn"
                      onClick={() => handleGenerateTranscription(webinar.id)}
                      title="Создать транскрипцию"
                    >
                      <FiFileText />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleEdit(webinar)}
                      title="Редактировать"
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleToggleStatus(webinar.id)}
                      title={webinar.status === 'open' ? 'Закрыть' : 'Открыть'}
                    >
                      {webinar.status === 'open' ? <FiLock /> : <FiUnlock />}
                    </button>
                    <button
                      className="icon-btn danger"
                      onClick={() => handleDelete(webinar.id)}
                      title="Удалить"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                )}
              </div>
              <Link to={`/webinars/${webinar.id}`} className="webinar-link">
                {!showCompanyCover && <h3>{webinar.title}</h3>}
                {webinar.speaker_data && (
                  <p className="speaker-name">{webinar.speaker_data.full_name}</p>
                )}
                {!!getRecordingPreview(webinar) && (
                  <p className="recording-preview">{getRecordingPreview(webinar)}</p>
                )}
                <p className="recording-date">
                  {new Date(webinar.recording_date).toLocaleDateString('ru-RU')}
                </p>
                {viewedByWebinarId.get(webinar.id)?.last_position_seconds > 0 && (
                  <p className="recording-date">
                    Продолжить с {formatDuration(viewedByWebinarId.get(webinar.id).last_position_seconds)}
                  </p>
                )}
                <div className="webinar-stats">
                  <span><FiEye /> {webinar.views_count}</span>
                  <span>❤️ {webinar.likes_count}</span>
                  {!isClient && <span>👎 {webinar.dislikes_count || 0}</span>}
                  {!isClient && <span>💬 {webinar.comments_count}</span>}
                </div>
              </Link>
            </div>
            );
              })}
            </div>
          </div>
        ))
        ) : (
        <div className="empty-state">
          <p>Нет вебинаров</p>
        </div>
        )}
        </>
        </div>
      )}

      {(isClient || activeSection === 'future') && (
        <div
          ref={futureSectionRef}
          className={`future-webinars-section ${isClient && activeSection !== 'future' ? 'client-tab-hidden' : ''}`}
        >
          <div className="future-filters-wrap">
            <div className="mobile-filters-toggle-wrap">
              <button
                type="button"
                className="btn btn-outline mobile-filters-toggle-btn"
                onClick={() => setShowMobileFutureFilters((prev) => !prev)}
              >
                {showMobileFutureFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
              </button>
            </div>
            <div className={`webinars-filters ${showMobileFutureFilters ? 'mobile-open' : ''}`}>
              <select
                value={futureFilters.year}
                onChange={(e) => setFutureFilters({ ...futureFilters, year: e.target.value })}
                className="filter-select"
              >
                <option value="">Все годы</option>
                {futureUniqueYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select
                value={futureFilters.month}
                onChange={(e) => setFutureFilters({ ...futureFilters, month: e.target.value })}
                className="filter-select"
              >
                <option value="">Все месяцы</option>
                {MONTH_NAMES.map((name, index) => (
                  <option key={name} value={index + 1}>{name}</option>
                ))}
              </select>
              <select
                value={futureFilters.speaker}
                onChange={(e) => setFutureFilters({ ...futureFilters, speaker: e.target.value })}
                className="filter-select"
              >
                <option value="">Все спикеры</option>
                {speakers.map((speaker) => (
                  <option key={speaker.id} value={speaker.id}>{speaker.full_name}</option>
                ))}
              </select>
              <select
                value={futureFilters.medication}
                onChange={(e) => setFutureFilters({ ...futureFilters, medication: e.target.value })}
                className="filter-select"
              >
                <option value="">Все препараты</option>
                {medications.map((medication) => (
                  <option key={medication.id} value={medication.id}>{medication.name}</option>
                ))}
              </select>
              <select
                value={futureFilters.sortBy}
                onChange={(e) => setFutureFilters({ ...futureFilters, sortBy: e.target.value })}
                className="filter-select"
              >
                <option value="scheduled_at">Сначала ближайшие</option>
                <option value="-scheduled_at">Сначала дальние</option>
                <option value="title">По теме (А-Я)</option>
                <option value="-title">По теме (Я-А)</option>
              </select>
            </div>
            {(futureFilters.year || futureFilters.month || futureFilters.speaker || futureFilters.medication) && (
              <div className="filters-reset">
                <button
                  className="btn btn-outline"
                  onClick={() =>
                    setFutureFilters({
                      year: '',
                      month: '',
                      speaker: '',
                      medication: '',
                      sortBy: 'scheduled_at',
                    })
                  }
                >
                  Сбросить фильтры
                </button>
              </div>
            )}
          </div>

          {isModerator && (
            <div className="schedule-email-panel">
              <h3>Рассылка расписания на email</h3>
              <p className="schedule-email-hint">
                {companyScheduleSettings.name && (
                  <>Фирма: <strong>{companyScheduleSettings.name}</strong>. </>
                )}
                {companyScheduleSettings.smtp_configured ? (
                  <>SMTP: <strong>{companyScheduleSettings.smtp_host}</strong>. </>
                ) : (
                  <>SMTP фирмы не задан — используется сервер из .env. </>
                )}
                {companyScheduleSettings.schedule_from_email ? (
                  <>From: <strong>{companyScheduleSettings.schedule_from_email}</strong>. </>
                ) : (
                  <>From не задан — будет SMTP логин или системный email. </>
                )}
                {companyScheduleSettings.default_registration_url && (
                  <>Ссылка на запись по умолчанию:{' '}
                    <a href={companyScheduleSettings.default_registration_url} target="_blank" rel="noopener noreferrer">
                      {companyScheduleSettings.default_registration_url}
                    </a>
                  </>
                )}
              </p>
              <div className="schedule-email-row">
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="email1@example.com email2@example.com или через запятую"
                  value={scheduleEmailList}
                  onChange={(e) => setScheduleEmailList(e.target.value)}
                />
                <select
                  className="form-select schedule-days-select"
                  value={scheduleEmailDays}
                  onChange={(e) => setScheduleEmailDays(Number(e.target.value))}
                >
                  <option value={7}>На 7 дней</option>
                  <option value={14}>На 14 дней</option>
                  <option value={30}>На 30 дней</option>
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSendScheduleEmail}
                  disabled={scheduleEmailSending}
                >
                  <FiMail /> {scheduleEmailSending ? 'Отправка...' : 'Отправить расписание'}
                </button>
              </div>
            </div>
          )}

          {showFutureForm && (
            <form className="future-webinar-form" onSubmit={handleCreateOrUpdateFutureWebinar}>
              <h3>{editingFutureWebinar ? 'Редактировать будущий вебинар' : 'Добавить будущий вебинар'}</h3>
              <input
                className="form-input"
                placeholder="Тема"
                value={futureFormData.title}
                onChange={(e) => setFutureFormData({ ...futureFormData, title: e.target.value })}
                required
              />
              <input
                type="datetime-local"
                className="form-input"
                value={futureFormData.scheduled_at}
                onChange={(e) => setFutureFormData({ ...futureFormData, scheduled_at: e.target.value })}
                required
              />
              <textarea
                className="form-textarea"
                rows="4"
                placeholder="Описание"
                value={futureFormData.description}
                onChange={(e) => setFutureFormData({ ...futureFormData, description: e.target.value })}
              />
              <input
                className="form-input"
                placeholder="Адрес проведения"
                value={futureFormData.location}
                onChange={(e) => setFutureFormData({ ...futureFormData, location: e.target.value })}
              />
              <input
                type="url"
                className="form-input"
                placeholder={
                  companyScheduleSettings.default_registration_url
                    ? `Ссылка на регистрацию (по умолчанию: ${companyScheduleSettings.default_registration_url})`
                    : 'Ссылка на регистрацию'
                }
                value={futureFormData.registration_url}
                onChange={(e) => setFutureFormData({ ...futureFormData, registration_url: e.target.value })}
              />

              <div className="future-speakers-block">
                <label>Спикеры</label>
                {futureSpeakerIds.map((speakerId, index) => (
                  <div key={`future-speaker-${index}`} className="future-speaker-row">
                    <select
                      className="form-select"
                      value={speakerId}
                      onChange={(e) => updateFutureSpeakerField(index, e.target.value)}
                    >
                      <option value="">Выберите спикера</option>
                      {speakers.map((speaker) => (
                        <option key={speaker.id} value={speaker.id}>
                          {speaker.full_name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      onClick={() => removeFutureSpeakerField(index)}
                    >
                      Удалить
                    </button>
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addFutureSpeakerField}>
                  <FiPlus /> Добавить спикера
                </button>
              </div>

              <div className="future-medications-block">
                <label>Препараты</label>
                <div className="future-medications-checkboxes">
                  {medications.length > 0 ? (
                    medications.map((med) => {
                      const checked = futureMedicationIds.includes(String(med.id));
                      return (
                        <label key={med.id} className="future-medication-check">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const id = String(med.id);
                              setFutureMedicationIds((prev) =>
                                prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
                              );
                            }}
                          />
                          <span>{med.name}</span>
                        </label>
                      );
                    })
                  ) : (
                    <span className="future-medications-empty">Справочник препаратов пуст</span>
                  )}
                </div>
                {futureMedicationIds.length > 0 && (
                  <div className="future-medications-selected">
                    Выбрано: {futureMedicationIds.length}
                  </div>
                )}
              </div>

              <div className="future-form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingFutureWebinar ? 'Сохранить' : 'Добавить'}
                </button>
                <button type="button" className="btn btn-outline" onClick={resetFutureForm}>
                  Отмена
                </button>
              </div>
            </form>
          )}

          {isModerator && companyLegendItems.length > 0 && (
            <div className="company-cover-legend" aria-label="Цвета фирм">
              {companyLegendItems.map((c) => (
                <span key={`future-${c.id || c.slug}`} className="company-cover-legend-item">
                  <span
                    className="company-cover-legend-swatch"
                    style={{ background: buildCompanyCoverGradient(c.primary, c.secondary, c.id) }}
                  />
                  {c.name}
                </span>
              ))}
            </div>
          )}

          {filteredFutureWebinars.length > 0 ? (
            <div className="future-webinars-grid">
              {filteredFutureWebinars.map((item) => {
                const companyCover = getWebinarCompanyColors(item);
                const companyGradient =
                  isModerator && companyCover.name
                    ? buildCompanyCoverGradient(companyCover.primary, companyCover.secondary, item.id)
                    : null;
                const medCoverKind = companyGradient ? null : getFutureAnnouncementCoverKind(item.medications_data);
                const coverKind = companyGradient ? 'company' : medCoverKind;
                const coverGradient =
                  companyGradient
                    || (medCoverKind === 'fbio'
                      ? futureAnnouncementCoverGradient(
                        FUTURE_COVER_PALETTE_FBIO,
                        fbioAnnouncementCoverSeed(item.id, item.medications_data)
                      )
                      : medCoverKind === 'line'
                        ? futureAnnouncementCoverGradient(FUTURE_COVER_PALETTE_LINE, Number(item.id) + 97)
                        : null);
                return (
                <div
                  key={item.id}
                  className={`future-webinar-card ${isClient ? 'client-future-card' : ''} ${expandedFutureIds.includes(item.id) ? 'expanded' : ''} ${coverGradient ? 'future-webinar-card--cover' : ''}`}
                  onClick={() => {
                    if (item.description || item.location) {
                      toggleFutureDetails(item.id);
                    }
                  }}
                >
                  {coverGradient ? (
                    <>
                      <div
                        className={`future-webinar-cover future-webinar-cover--${coverKind}`}
                        style={{ background: coverGradient }}
                      >
                        {isModerator && (
                          <div className="future-webinar-cover-admin" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditFutureWebinar(item);
                              }}
                              title="Редактировать"
                            >
                              <FiEdit />
                            </button>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFutureWebinar(item.id);
                              }}
                              title="Удалить"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        )}
                        {companyGradient && (
                          <span className="webinar-company-cover-badge future-webinar-company-badge">
                            {companyCover.name}
                          </span>
                        )}
                        <h3 className="future-webinar-cover-title">{item.title}</h3>
                      </div>
                      <div className="future-announcement-meta">
                        <span className="future-announcement-meta-item future-announcement-time">
                          <FiClock aria-hidden className="future-announcement-meta-icon" />
                          <span>{formatFutureAnnouncementMSK(item.scheduled_at)}</span>
                        </span>
                        {!!item.speakers_data?.length && (
                          <span className="future-announcement-meta-item future-announcement-speakers">
                            <FiUser aria-hidden className="future-announcement-meta-icon" />
                            <span>{item.speakers_data.map((speaker) => speaker.full_name).join(', ')}</span>
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="future-webinar-header">
                        <h3>{item.title}</h3>
                        {isModerator && (
                          <div className="webinar-actions">
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditFutureWebinar(item);
                              }}
                              title="Редактировать"
                            >
                              <FiEdit />
                            </button>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteFutureWebinar(item.id);
                              }}
                              title="Удалить"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="future-webinar-date">
                        {new Date(item.scheduled_at).toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      {!!item.speakers_data?.length && (
                        <p className="speaker-name">{item.speakers_data.map((speaker) => speaker.full_name).join(', ')}</p>
                      )}
                    </>
                  )}
                  {(item.description || item.location) && (
                    <p className="future-details-hint">
                      {expandedFutureIds.includes(item.id) ? 'Нажмите на карточку, чтобы скрыть описание' : 'Нажмите на карточку, чтобы показать описание'}
                    </p>
                  )}
                  {expandedFutureIds.includes(item.id) && (
                    <div className="future-webinar-details-expanded">
                      {item.description && <p className="future-webinar-description">{item.description}</p>}
                      {item.location && <p className="future-webinar-location">Адрес: {item.location}</p>}
                      {!!item.medications_data?.length && (
                        <p className="future-webinar-location">
                          Препараты: {item.medications_data.map((medication) => medication.name).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="future-webinar-links">
                    {item.registration_url && (
                      <a
                        href={item.registration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline btn-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Регистрация
                      </a>
                    )}
                    {canShare && (
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareFutureWebinar(item);
                        }}
                      >
                        <FiShare2 /> Поделиться
                      </button>
                    )}
                    <a
                      href={getGoogleCalendarUrl(item)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FiCalendar /> Добавить в календарь
                    </a>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>Будущих вебинаров пока нет</p>
            </div>
          )}
        </div>
      )}

      {isClient && (
        <div
          ref={viewedSectionRef}
          className={`future-webinars-section ${activeSection !== 'viewed' ? 'client-tab-hidden' : ''}`}
        >
          {viewedWebinars.length > 0 ? (
            <div className="future-webinars-grid">
              {viewedWebinars.map((entry) => (
                <div key={entry.id} className="future-webinar-card client-viewed-card">
                  <div className={`client-content-badge ${entry.is_completed ? 'done' : 'in-progress'}`}>
                    {entry.is_completed ? 'Просмотрено' : 'В процессе'}
                  </div>
                  <div className="future-webinar-header">
                    <h3>{entry.webinar?.title || 'Вебинар'}</h3>
                  </div>
                  {entry.webinar?.recording_date && (
                    <p className="future-webinar-date">
                      {new Date(entry.webinar.recording_date).toLocaleDateString('ru-RU')}
                    </p>
                  )}
                  {entry.webinar?.speaker_data?.full_name && (
                    <p className="speaker-name">{entry.webinar.speaker_data.full_name}</p>
                  )}
                  <p className="future-webinar-location">
                    Остановились на: {formatDuration(entry.last_position_seconds)}
                  </p>
                  <p className="future-webinar-location">
                    Статус: {entry.is_completed ? 'Просмотрено' : 'В процессе'}
                  </p>
                  <div className="future-webinar-links">
                    {entry.webinar?.id && (
                      <Link to={`/webinars/${entry.webinar.id}`} className="btn btn-primary btn-sm client-cta-btn">
                        Продолжить просмотр
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>Пока нет просмотренных вебинаров</p>
            </div>
          )}
        </div>
      )}

      {saveStatus === 'success' && (
        <div className="status-bar success">
          <span>✓ Вебинар успешно сохранен</span>
        </div>
      )}

      {isModerator && scheduleImportPreview && (
        <div
          className="schedule-import-overlay"
          onClick={() => !scheduleImportSaving && setScheduleImportPreview(null)}
        >
          <div className="schedule-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="schedule-import-header">
              <h2>Импорт расписания</h2>
              <button
                type="button"
                className="schedule-import-close"
                onClick={() => setScheduleImportPreview(null)}
                aria-label="Закрыть"
              >
                <FiX />
              </button>
            </div>
            <p className="schedule-import-hint">
              Файл: <strong>{scheduleImportPreview.source_filename}</strong>
              {scheduleImportPreview.location ? <> · Адрес: {scheduleImportPreview.location}</> : null}
              . Снимите галочки с лишних строк, затем добавьте в «Будущие вебинары».
              Ссылка ставится только если она есть у конкретного мероприятия в файле.
              Если ссылки нет — это очное событие, поле останется пустым.
            </p>
            <div className="schedule-import-list">
              {(scheduleImportPreview.items || []).map((item, index) => (
                <label
                  key={`${item.scheduled_at}-${index}`}
                  className={`schedule-import-item ${item.is_duplicate ? 'is-duplicate' : ''} ${item.selected ? 'is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={!!item.selected}
                    disabled={item.is_duplicate}
                    onChange={() => toggleScheduleImportItem(index)}
                  />
                  <div className="schedule-import-item-body">
                    <div className="schedule-import-item-title">{item.title}</div>
                    <div className="schedule-import-item-meta">
                      {formatFutureAnnouncementMSK(item.scheduled_at) || item.scheduled_at}
                      {item.speaker_name ? ` · ${item.speaker_name}` : ''}
                      {item.matched_speakers?.length ? ` → ${item.matched_speakers.join(', ')}` : (item.speaker_name ? ' (спикер не найден в справочнике)' : '')}
                    </div>
                    {item.matched_medications?.length > 0 && (
                      <div className="schedule-import-item-meds">
                        Препараты: {item.matched_medications.join(', ')}
                      </div>
                    )}
                    <div className="schedule-import-item-meds">
                      {item.registration_url
                        ? <>Ссылка: {item.registration_url}</>
                        : 'Ссылка не указана — очное мероприятие'}
                    </div>
                    {item.is_duplicate && (
                      <div className="schedule-import-item-dup">Уже есть в расписании — будет пропущено</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="schedule-import-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setScheduleImportPreview(null)}
                disabled={scheduleImportSaving}
              >
                Отмена
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmScheduleImport}
                disabled={scheduleImportSaving || !(scheduleImportPreview.items || []).some((item) => item.selected && !item.is_duplicate)}
              >
                {scheduleImportSaving
                  ? 'Добавление...'
                  : `Добавить в расписание (${(scheduleImportPreview.items || []).filter((item) => item.selected && !item.is_duplicate).length})`}
              </button>
            </div>
          </div>
        </div>
      )}
      {isModerator && (
        <WebinarModal
          open={modalOpen}
          onClose={handleCloseModal}
          webinar={editingWebinar}
        />
      )}
      {showScrollTop && (
        <button
          type="button"
          className="scroll-top-btn"
          onClick={scrollToTop}
          title="Наверх"
        >
          <FiChevronUp />
        </button>
      )}
    </div>
  );
};

export default Webinars;
